import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Trash2 } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getGetKnowledgeDocumentsQueryKey,
  useArchiveKnowledgeDocument,
  useGetKnowledgeDocuments,
} from "~/api/default/default";
import { UpdateKnowledgeDocumentBodyStatus } from "~/api/model";
import { useGetProjectIndexedPages } from "~/api/projects/projects";
import { useGetCurrentUser } from "~/api/users/users";
import PagePreview from "~/assets/page_preview.jpg";
import { Loader } from "~/components/Loader";
import { LoaderRays } from "~/components/loader-rays";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { IndexStatusBadge } from "~/features/case/components/IndexStatusBadge";
import { DocumentsDropzone } from "~/features/case/components/list/DocumentsDropzone";
import { UploadButton } from "~/features/case/components/UploadButton";
import { formatBytes, useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { useReindexKnowledgeBase } from "~/lib/knowledge/reindex";
import { useKnowledgeDocumentUpload } from "../hooks/useKnowledgeDocumentUpload";

interface TableHeader {
  id: string;
  defaultMessage: string;
  className?: string;
}
export const SettingsKnowledge: FC = () => {
  const intl = useIntl();
  const { toast } = useToast();
  const { organizationId } = useOrganizationParams();
  const { uploadKnowledgeDocuments } = useKnowledgeDocumentUpload({
    organizationId,
  });
  const { data: currentUser } = useGetCurrentUser();
  const { mutateAsync: archiveKnowledgeDocument } =
    useArchiveKnowledgeDocument();
  const { mutateAsync: reindexKnowledgeBase, isPending: isReindexing } =
    useReindexKnowledgeBase();
  const { data: knowledgeDocuments, isLoading: isLoadingKnowledgeDocuments } =
    useGetKnowledgeDocuments(organizationId);
  const queryClient = useQueryClient();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [documentToArchive, setDocumentToArchive] = useState<{
    id: string;
    filename: string;
  } | null>(null);

  const { data: indexStatus } = useGetProjectIndexedPages(
    organizationId,
    "knowledge-base",
    { query: { enabled: true } }
  );

  const handleReindex = async () => {
    if (!currentUser?.isAdminViewEnabled) {
      return;
    }

    try {
      const result = await reindexKnowledgeBase(organizationId);
      toast({
        title: intl.formatMessage({
          id: "settings.knowledge.reindexStarted",
          defaultMessage: "Reindex started",
        }),
        description: intl.formatMessage(
          {
            id: "settings.knowledge.reindexStartedDesc",
            defaultMessage:
              "Processing {count} pages. This may take a few minutes.",
          },
          { count: result.total_documents }
        ),
      });
    } catch (error) {
      console.error("Error reindexing knowledge base:", error);
      toast({
        title: intl.formatMessage({
          id: "settings.knowledge.reindexError",
          defaultMessage: "Reindex failed",
        }),
        description: intl.formatMessage({
          id: "settings.knowledge.reindexErrorDesc",
          defaultMessage: "Failed to start reindex. Please try again.",
        }),
        variant: "destructive",
      });
    }
  };

  const tableHeaders: TableHeader[] = [
    {
      id: "settings.knowledge.table.name",
      defaultMessage: "Name",
      className: "px-4",
    },
    {
      id: "settings.knowledge.table.pages",
      defaultMessage: "Pages",
      className: "px-4",
    },
    {
      id: "settings.knowledge.table.size",
      defaultMessage: "Size",
      className: "px-4",
    },
    {
      id: "settings.knowledge.table.actions",
      defaultMessage: "Actions",
      className: "px-4 text-right",
    },
  ];

  const handleUpload = async (files: File[]) => {
    try {
      await uploadKnowledgeDocuments(files);
    } catch (error) {
      console.error("Error uploading files:", error);
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "settings.knowledge.uploadError",
          defaultMessage: "Upload failed",
        }),
        description: intl.formatMessage({
          id: "settings.knowledge.uploadErrorDescription",
          defaultMessage:
            "Some documents couldn't be uploaded. Please try again.",
        }),
      });
    }
  };

  const handleArchiveConfirm = async () => {
    if (!documentToArchive) return;

    try {
      await archiveKnowledgeDocument({
        organizationId,
        documentId: documentToArchive.id,
      });

      toast({
        title: intl.formatMessage({
          id: "settings.knowledge.archiveSuccess",
          defaultMessage: "Document archived successfully",
        }),
        description: intl.formatMessage({
          id: "settings.knowledge.archiveSuccess",
          defaultMessage: "Document archived successfully",
        }),
      });

      // Refetch documents list
      await queryClient.invalidateQueries({
        queryKey: getGetKnowledgeDocumentsQueryKey(organizationId),
      });
    } catch (error) {
      console.error("Error archiving document:", error);
      toast({
        title: intl.formatMessage({
          id: "settings.knowledge.archiveError",
          defaultMessage: "Failed to archive document",
        }),
        description: intl.formatMessage({
          id: "settings.knowledge.archiveError",
          defaultMessage: "Failed to archive document",
        }),
      });
    } finally {
      setDocumentToArchive(null);
    }
  };

  if (isLoadingKnowledgeDocuments)
    return (
      <div className="flex justify-center items-center w-full max-w-[924px] h-[50vh]">
        <Loader />
      </div>
    );

  return (
    <div className="flex flex-col gap-6 mt-2 w-full h-[calc(100dvh-190px-4rem-env(safe-area-inset-bottom))] sm:h-[calc(100vh-190px)]">
      <div className="flex flex-col sm:flex-row gap-4 shrink-0 justify-between">
        <div className="flex flex-col gap-2">
          <h3 className="text-2xl font-semibold">
            <FormattedMessage
              id="settings.knowledge"
              defaultMessage={"Knowledge"}
            />
          </h3>
          <span className="text-sm text-muted-foreground">
            <FormattedMessage
              id="settings.knowledge.hint"
              defaultMessage={
                "Upload documents to org-wide knowledge base for reference in chats and analyses"
              }
            />
          </span>
        </div>
        {knowledgeDocuments && knowledgeDocuments.length > 0 && (
          <div className="flex gap-2 items-center shrink-0 self-start">
            {currentUser?.isAdminViewEnabled && (
              <>
                <IndexStatusBadge status={indexStatus} />
                <Button
                  variant="outline"
                  onClick={handleReindex}
                  disabled={isReindexing}
                  className="gap-2"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isReindexing ? "animate-spin" : ""}`}
                  />
                  <FormattedMessage
                    id="settings.knowledge.reindex"
                    defaultMessage="Reindex"
                  />
                </Button>
              </>
            )}
            <UploadButton
              isModalOpen={isUploadDialogOpen}
              setIsModalOpen={setIsUploadDialogOpen}
              onFilesSelected={handleUpload}
              disabled={false}
              isKnowledge={true}
              organizationId={organizationId}
              autoCloseModal={true}
            />
          </div>
        )}
      </div>
      <div className="flex-1 h-full">
        {knowledgeDocuments && knowledgeDocuments.length > 0 ? (
          <Table className="w-full">
            <TableHeader>
              <TableRow className="text-zinc-500! text-sm! font-medium!">
                {tableHeaders.map((header) => (
                  <TableHead key={header.id} className={header.className}>
                    {header.id && (
                      <FormattedMessage
                        id={header.id}
                        defaultMessage={header.defaultMessage}
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {knowledgeDocuments?.map((document) => (
                <TableRow key={document.id}>
                  <TableCell className="font-semibold p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-12 h-12 min-w-12 min-h-12 rounded-md flex justify-center items-center">
                        {document.status ===
                        UpdateKnowledgeDocumentBodyStatus.UPLOADING ? (
                          <LoaderRays className="w-6 h-6 animate-spin text-blue-600" />
                        ) : (
                          <img
                            src={document?.previewUrl || PagePreview}
                            className="h-full object-cover pt-2"
                          />
                        )}
                      </div>
                      <span className="text-sm font-medium">
                        {document.filename}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground p-4">
                    {document.pageCount || "-"}
                  </TableCell>
                  <TableCell className="text-muted-foreground p-4">
                    {formatBytes(document.size)}
                  </TableCell>
                  <TableCell className="p-4 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={intl.formatMessage({
                        id: "settings.knowledge.archiveDocument",
                        defaultMessage: "Archive Document",
                      })}
                      onClick={() =>
                        setDocumentToArchive({
                          id: document.id,
                          filename: document.filename || "Unknown",
                        })
                      }
                      disabled={
                        document.status !==
                        UpdateKnowledgeDocumentBodyStatus.COMPLETED
                      }
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <DocumentsDropzone
            onDrop={handleUpload}
            className="w-full h-full"
            isKnowledge={true}
          />
        )}
      </div>

      <AlertDialog
        open={!!documentToArchive}
        onOpenChange={(open) => !open && setDocumentToArchive(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <FormattedMessage
                id="settings.knowledge.archiveConfirm.title"
                defaultMessage="Archive Document"
              />
            </AlertDialogTitle>
            <AlertDialogDescription>
              <FormattedMessage
                id="settings.knowledge.archiveConfirm.description"
                defaultMessage='Are you sure you want to archive "{filename}"? This will remove it from the knowledge base.'
                values={{ filename: documentToArchive?.filename }}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <FormattedMessage
                id="settings.knowledge.archiveConfirm.cancel"
                defaultMessage="Cancel"
              />
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleArchiveConfirm}>
              <FormattedMessage
                id="settings.knowledge.archiveConfirm.confirm"
                defaultMessage="Archive"
              />
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
